import * as Dialog from '@radix-ui/react-dialog'
import { useSelector } from '@tanstack/react-form'
import classNames from 'classnames'
import { Suspense, useCallback, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { useShowMutationError } from '../../hooks/use-show-mutation-error.tsx'
import { Routes } from '../../routes.ts'
import { useToastService } from '../../services/toast-service.tsx'
import {
  usePostAiScan,
  usePostAnalyzeScan,
  usePostUploadSbom,
  usePostUploadScan,
  useSuspenseGetRepository,
} from '../../utils/api-client/api-hooks.ts'
import { isAiScannerTool } from '../../utils/detector-utils.ts'
import { initializeFirstBatch } from '../../utils/first-batch-tracking.ts'
import { ErrorBoundary } from '../error-boundaries/error-boundary'
import { Checkbox } from '../input/checkbox'
import { Spinner } from '../spinner.tsx'
import { SCMIcon } from '../third-party-branding/scm-icon'
import { Tooltip } from '../tooltip.tsx'
import * as styles from './add-findings-drawer.css.ts'
import {
  AddFindingsFooterActions,
  AddFindingsForm,
  type ProvideMethod,
  type SubmitPayload,
  useAddFindingsForm,
  useFormContext,
} from './add-findings-form.tsx'
import * as footerStyles from './floating-footer.css'
import { FloatingFooter } from './floating-footer.tsx'

export type AddFindingsDrawerProps = {
  repositoryId: string
  onCloseDrawer: () => unknown
}

export const AddFindingsDrawer: React.FC<AddFindingsDrawerProps> = ({ repositoryId, onCloseDrawer }) => {
  const drawerContentRef = useRef<HTMLDivElement>(null)
  const [autoAnalyze, setAutoAnalyze] = useState(false)

  const { handleFormSubmit, isPending } = useAddFindingsMutations({
    repositoryId,
    onCloseDrawer,
    autoAnalyze,
  })

  const form = useAddFindingsForm()

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        onCloseDrawer()
      }
    },
    [onCloseDrawer]
  )

  return (
    <Dialog.Root open={true} onOpenChange={handleOpenChange} modal={false}>
      <Dialog.Portal>
        <form.AppForm>
          <Dialog.Content
            ref={drawerContentRef}
            className={classNames(styles.content, footerStyles.drawerContentPaddingAdjustment)}
            data-has-floating-footer={true}
            onPointerDownOutside={event => event.preventDefault()}
            onInteractOutside={event => event.preventDefault()}
          >
            <Suspense fallback={<Spinner size="64" variant="cool" />}>
              <AddFindingsDrawerBody repositoryId={repositoryId} />
            </Suspense>
          </Dialog.Content>
          <FloatingFooter drawerContentRef={drawerContentRef}>
            <AutoAnalyzeToggle repositoryId={repositoryId} autoAnalyze={autoAnalyze} onChange={setAutoAnalyze} />
            <div className={styles.actionButtons}>
              <AddFindingsFooterActions onCancel={onCloseDrawer} isPending={isPending} onSubmit={handleFormSubmit} />
            </div>
          </FloatingFooter>
        </form.AppForm>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

const AddFindingsDrawerBody: React.FC<{ repositoryId: string }> = ({ repositoryId }) => {
  const { data: repository } = useSuspenseGetRepository({ repositoryId })
  return (
    <>
      <Dialog.Title className={styles.title}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
          <SCMIcon type={repository.type} size="medium" />
          <span className={styles.repoName}>{repository.name}</span>
        </div>
        Add findings
      </Dialog.Title>
      <Dialog.Description className={styles.description}>
        Manually designate tool findings for analysis
      </Dialog.Description>
      <ErrorBoundary fallbackTitle="Failed to load upload form">
        <AddFindingsForm repositoryId={repositoryId} />
      </ErrorBoundary>
    </>
  )
}

type AutoAnalyzeToggleProps = {
  repositoryId: string
  autoAnalyze: boolean
  onChange: (checked: boolean) => unknown
}

// For an upload this toggle decides whether the imported findings are analyzed afterwards. For the
// AI scanner it selects the scan mode instead — checked means the scan also triages and fixes what
// it generates — and there is no upload to speak of, so both the label and the tooltip change.
const AUTO_ANALYZE_COPY = {
  upload: {
    label: 'Automatically analyze all findings',
    tooltip:
      'It is recommended to manually select findings for analysis after upload, or to create an automated workflow',
  },
  aiScanner: {
    label: 'Automatically triage and fix findings',
    tooltip:
      'Pixee triages each finding it generates and proposes a fix. Leave this off to generate findings only and choose what to analyze yourself.',
  },
} as const

const AutoAnalyzeToggle: React.FC<AutoAnalyzeToggleProps> = ({ repositoryId, autoAnalyze, onChange }) => {
  const form = useFormContext()
  const selectedTool = useSelector(form.store, s => s.values.selectedTool)
  const copy = selectedTool && isAiScannerTool(selectedTool) ? AUTO_ANALYZE_COPY.aiScanner : AUTO_ANALYZE_COPY.upload

  const autoAnalyzeId = `autoAnalyze-addFindings-${repositoryId}`
  return (
    <div className={styles.autoAnalyzeContainer}>
      <Checkbox
        id={autoAnalyzeId}
        checked={autoAnalyze}
        onCheckedChange={checked => onChange(checked === true)}
        ariaLabel={copy.label}
      />
      <label htmlFor={autoAnalyzeId} className={styles.autoAnalyzeLabel}>
        {copy.label}
      </label>
      <Tooltip label="Info about auto analysis" side="top" sideOffset={8} width={280}>
        {copy.tooltip}
      </Tooltip>
    </div>
  )
}

type UseAddFindingsMutationsArgs = {
  repositoryId: string
  onCloseDrawer: () => unknown
  autoAnalyze: boolean
}

type UseAddFindingsMutationsResult = {
  handleFormSubmit: (payload: SubmitPayload) => unknown
  isPending: boolean
}

const useAddFindingsMutations = ({
  repositoryId,
  onCloseDrawer,
  autoAnalyze,
}: UseAddFindingsMutationsArgs): UseAddFindingsMutationsResult => {
  const navigate = useNavigate()
  const { addToast } = useToastService()

  const autoAnalyzeRef = useRef(autoAnalyze)
  autoAnalyzeRef.current = autoAnalyze
  const effectiveProvideMethodRef = useRef<ProvideMethod>('upload')

  const navigateToScan = useCallback(
    (scanId: string) => {
      onCloseDrawer()
      navigate(Routes.ScanDetailsPage.createPath({ scanId }))
    },
    [onCloseDrawer, navigate]
  )

  const showAnalysisError = useShowMutationError({ prefix: 'Unable to trigger analysis' })
  const analyzeScanMutation = usePostAnalyzeScan({
    onSuccess: (_data, variables) => {
      addToast({ message: <>Analysis triggered</>, variant: 'success' })
      initializeFirstBatch(variables.scanId, 1)
      navigateToScan(variables.scanId)
    },
    onError: (error, variables, ...rest) => {
      showAnalysisError(error, variables, ...rest)
      navigateToScan(variables.scanId)
    },
  })

  const showUploadError = useShowMutationError()
  const uploadScanMutation = usePostUploadScan({
    onSuccess: ({ id: scanId }) => {
      addToast({
        message: (
          <>
            {effectiveProvideMethodRef.current === 'upload'
              ? 'File successfully uploaded'
              : 'Findings successfully imported'}
          </>
        ),
        variant: 'success',
      })
      if (autoAnalyzeRef.current) {
        analyzeScanMutation.mutate({ scanId })
      } else {
        navigateToScan(scanId)
      }
    },
    onError: showUploadError,
  })

  const showSbomError = useShowMutationError({ prefix: 'Unable to upload SBOM' })
  const uploadSbomMutation = usePostUploadSbom({
    onSuccess: () => {
      addToast({ message: <>SBOM uploaded successfully.</>, variant: 'success' })
    },
    onError: showSbomError,
  })

  // The Pixee AI Scanner has no file to upload, and its 202 carries an analysis-service scan id —
  // not a platform `Scan` id, which exists only once generation finishes and the findings are
  // ingested (see `ScansResource#events` in pixee-platform). So there is no scan page to open:
  // the drawer hands the user to the repository's AI detection tab, which streams that same id for
  // progress and lists each batch as it lands. The auto-triage toggle selects the mode (ENH-590).
  const showAiScanError = useShowMutationError({ prefix: 'Unable to generate findings' })
  const aiScanMutation = usePostAiScan({
    onSuccess: () => {
      addToast({ message: <>Generating findings for this repository.</>, variant: 'success' })
      onCloseDrawer()
      navigate(
        Routes.RepositoryDetailsPage.createPath({ repositoryId, tab: 'detections', detectionSource: 'ai-detection' })
      )
    },
    onError: showAiScanError,
  })

  const handleFormSubmit = useCallback(
    (payload: SubmitPayload) => {
      if (isAiScannerTool(payload.scanBody.metadata.tool)) {
        aiScanMutation.mutate({
          repositoryId,
          body: { mode: autoAnalyzeRef.current ? 'generate_triage_fix' : 'generate' },
        })
        return
      }
      effectiveProvideMethodRef.current = payload.effectiveProvideMethod
      uploadScanMutation.mutate({ repositoryId, body: payload.scanBody })
      if (payload.sbomBody) {
        uploadSbomMutation.mutate({ repositoryId, body: payload.sbomBody })
      }
    },
    [repositoryId, uploadScanMutation.mutate, uploadSbomMutation.mutate, aiScanMutation.mutate]
  )

  const isPending =
    uploadScanMutation.isPending ||
    uploadSbomMutation.isPending ||
    analyzeScanMutation.isPending ||
    aiScanMutation.isPending

  return { handleFormSubmit, isPending }
}
